<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: BloodRelativesDiagram                                 |
| Section: components                                              |
| Purpose: Zoomable blood-relatives diagram with pan controls.     |
+------------------------------------------------------------------+
-->
<template>
    <div ref="diagramContainer" class="blood-relatives-wrap"
        :class="{ 'blood-relatives-wrap--fullscreen': isTreeFullscreen }">
        <div id="zoom-buttons" :aria-label="$t('bloodRelativesDiagram.controls.ariaLabel')">
            <button type="button" @click="zoomIn" :aria-label="$t('bloodRelativesDiagram.controls.zoomIn')">
                <img src="@/assets/icons/zoom_in_red.svg" alt="Zoom In">
            </button>
            <button type="button" @click="zoomOut" :aria-label="$t('bloodRelativesDiagram.controls.zoomOut')">
                <img src="@/assets/icons/zoom_out_red.svg" alt="Zoom Out">
            </button>
        </div>
        <button id="fullscreen-button" type="button" @click="toggleFullscreen"
            :title="isTreeFullscreen ? $t('bloodRelativesDiagram.controls.exitFullscreen') : $t('bloodRelativesDiagram.controls.fullscreen')"
            :aria-label="isTreeFullscreen ? $t('bloodRelativesDiagram.controls.exitFullscreen') : $t('bloodRelativesDiagram.controls.fullscreen')">
            <img v-if="!isTreeFullscreen" src="@/assets/icons/fullscreen.svg"
                :alt="$t('bloodRelativesDiagram.controls.fullscreen')">
            <img v-else src="@/assets/icons/fullscreen_exit.svg"
                :alt="$t('bloodRelativesDiagram.controls.exitFullscreen')">
        </button>

        <div v-if="loading" class="status">{{ $t('bloodRelativesDiagram.loading') }}</div>
        <div v-else-if="error" class="status status--error">{{ error }}</div>

        <div v-else ref="viewport" class="diagram-viewport" :class="{ 'diagram-viewport--panel-open': panel.visible }"
            @wheel="onWheel" @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerUp"
            @pointerleave="onPointerUp" @pointercancel="onPointerUp">
            <div class="diagram-zoom-hint">
                {{ cooperativeGestures ? $t('bloodRelativesDiagram.zoomHintCooperative') : $t('bloodRelativesDiagram.zoomHintDirect') }}
            </div>

            <div ref="captureRoot" class="diagram-canvas-shell">
                <div class="diagram-world" :style="worldTransformStyle">
                    <svg class="diagram-connectors" :width="canvas.width" :height="canvas.height"
                        xmlns="http://www.w3.org/2000/svg">
                        <path v-for="connector in visibleConnectors" :key="connector.key" :d="connector.d"
                            class="connector" :class="{ half: connector.half }" />
                    </svg>

                    <div class="self-node" :style="nodePositionStyle(selfNode)" aria-label="Jeffrey Thumann, u">
                        <img v-if="selfPhotoUrl" :src="selfPhotoUrl" alt="Profiel van Jeffrey Thumann"
                            class="self-avatar" />
                        <div v-else class="self-avatar self-avatar--fallback" aria-hidden="true">JT</div>
                        <div class="self-label">Jeffrey</div>
                    </div>

                    <button v-for="node in visibleNodes" :key="node.key" type="button" class="relative-node"
                        :class="{ 'relative-node--half': node.half, 'relative-node--spine': isSpineNode(node) }"
                        :style="nodePositionStyle(node)"
                        :aria-label="$t('bloodRelativesDiagram.ariaLabels.peopleInGroup', { count: node.count, label: node.label })"
                        @click="listIndividuals(node.path, getRelationshipLabel(node.path, node.half), node.half)"
                        @mouseenter="showTooltip($event, node)" @mousemove="moveTooltip($event)"
                        @mouseleave="hideTooltip">
                        <span class="relative-count">{{ node.count }}</span>
                        <span class="relative-label" :style="labelFontStyle(node.label)">{{ node.label }}</span>
                    </button>
                </div>
            </div>

            <div v-if="tooltip.visible" class="node-tooltip" :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }">
                {{ tooltip.text }}
            </div>

            <Transition name="relatives-panel">
                <aside v-if="panel.visible" class="relatives-panel"
                    :aria-label="$t('bloodRelativesDiagram.ariaLabels.panelLabel')"
                    @pointerdown.self.prevent.stop="closePanel">
                    <div class="relatives-panel__overlay" @pointerdown.prevent.stop="closePanel"></div>

                    <button class="relatives-panel__close-ext" @click="closePanel"
                        :aria-label="$t('bloodRelativesDiagram.panel.close')"
                        :title="$t('bloodRelativesDiagram.panel.closeTitle')">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                    </button>

                    <div class="relatives-panel__sheet">
                        <div class="relatives-panel__header">
                            <h2 class="relatives-panel__title">{{ panel.title }}</h2>
                            <p class="relatives-panel__subtitle">
                                {{ panel.individuals.length }} {{ panel.individuals.length === 1 ?
                                    $t('bloodRelativesDiagram.panel.person') : $t('bloodRelativesDiagram.panel.people') }}
                            </p>
                        </div>

                        <div v-if="panel.loading" class="relatives-panel__body relatives-panel__body--status">
                            <LoadingSpinner />
                        </div>
                        <div v-else-if="panel.error"
                            class="relatives-panel__body relatives-panel__body--status relatives-panel__error">
                            {{ panel.error }}
                        </div>
                        <div v-else class="relatives-panel__body">
                            <div class="relatives-panel__cards">
                                <PersonCard v-for="person in panel.individuals" :key="getPersonNodeKey(person)"
                                    :photoSrc="person.photoPath || ''" :name="person.name || ''"
                                    :years="formatPersonYears(person)" :gender="person.sex || 'M'"
                                    :personID="person.personID" :firstname="person.firstname" :lastname="person.lastname"
                                    :living="Boolean(person.living)"
                                    :href="person.personID ? `/person/${person.personID}` : 'javascript:void(0)'"
                                    :placeholder="person.placeholder"
                                    :isSmall="true" />
                            </div>
                        </div>
                    </div>
                </aside>
            </Transition>
        </div>
    </div>
</template>

<script>
import {
    fetchBloodRelativesStats,
    fetchBloodRelativesStaticFile,
    fetchPersonDefaultPhoto,
} from '@/api/apiService';
import { useAuth } from '@/composables/useAuth';
import { formatYears } from '@/utils/dateUtils';
import { debugLog } from '@/utils/logger';
import PersonCard from '@/atoms/PersonCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { getPersonNodeKey, hasLivingPersonAccess, isLivingPlaceholder } from '@/utils/personPrivacy';

const STEP_X = 140;
const STEP_Y = 140;
const SELF_X = 200;
const SELF_Y = 2800;
const NODE_W = 110;
const NODE_H = 90;

export default {
    name: 'BloodRelativesDiagram',

    components: {
        PersonCard,
        LoadingSpinner,
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    props: {
        cooperativeGestures: {
            type: Boolean,
            default: true
        }
    },

    data() {
        debugLog('BloodRelativesDiagram data() called');
        return {
            loading: true,
            error: null,
            isFullscreen: false,
            isPseudoFullscreen: false,
            savedScrollY: 0,
            treeTotal: 0,
            nodes: [],
            nodesByPath: {},
            nodesByKey: {},
            groupsByKey: {},
            connectors: [],
            selfPhotoUrl: null,
            selfPosition: {
                x: SELF_X,
                y: SELF_Y,
            },
            canvas: {
                width: 3600,
                height: 3600,
            },
            scale: 1,
            minScale: 0.05,
            maxScale: 2,
            panX: 0,
            panY: 0,
            viewport: {
                width: 1200,
                height: 720,
            },
            pointer: {
                dragging: false,
                pointerId: null,
                startX: 0,
                startY: 0,
                startPanX: 0,
                startPanY: 0,
            },
            rafState: {
                // Transform updates are batched in requestAnimationFrame to keep drag/zoom smooth.
                transformRaf: 0,
                nextScale: null,
                nextPanX: null,
                nextPanY: null,
            },
            tooltip: {
                visible: false,
                x: 0,
                y: 0,
                text: '',
            },
            panel: {
                visible: false,
                loading: false,
                title: '',
                individuals: [],
                error: null,
            },
            pageScrollLock: {
                active: false,
                previousOverflow: '',
                previousTouchAction: '',
            },
            resizeObserver: null,
        };
    },

    computed: {
        isTreeFullscreen() {
            return this.isFullscreen || this.isPseudoFullscreen;
        },
        selfNode() {
            return {
                x: this.selfPosition.x,
                y: this.selfPosition.y,
            };
        },
        worldTransformStyle() {
            return {
                width: `${this.canvas.width}px`,
                height: `${this.canvas.height}px`,
                transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`,
            };
        },
        worldBounds() {
            // Convert viewport coordinates to world coordinates for virtualized filtering.
            const left = -this.panX / this.scale;
            const top = -this.panY / this.scale;
            const width = this.viewport.width / this.scale;
            const height = this.viewport.height / this.scale;
            return { left, top, right: left + width, bottom: top + height };
        },
        useVirtualization() {
            return this.nodes.length > 200;
        },
        visibleNodes() {
            if (!this.useVirtualization) return this.nodes;
            const margin = 280;
            return this.nodes.filter((node) => (
                node.x >= this.worldBounds.left - margin
                && node.x <= this.worldBounds.right + margin
                && node.y >= this.worldBounds.top - margin
                && node.y <= this.worldBounds.bottom + margin
            ));
        },
        visibleNodePaths() {
            return new Set(this.visibleNodes.map(node => node.key));
        },
        visibleConnectors() {
            // Only keep connectors whose endpoints are currently in or near the viewport.
            if (!this.useVirtualization) return this.connectors;
            return this.connectors.filter((connector) => {
                return connector.parentPath === 'self'
                    ? this.visibleNodePaths.has(connector.childKey)
                    : this.visibleNodePaths.has(connector.parentKey)
                    || this.visibleNodePaths.has(connector.childKey);
            });
        },
    },

    watch: {
        '$i18n.locale'() {
            // Re-compute node labels when the active locale changes
            this.nodes = this.nodes.map(node => ({
                ...node,
                label: this.getRelationshipLabel(node.path, node.half),
            }));
        },
        'panel.visible'(isVisible) {
            if (isVisible) {
                this.enablePanelMode();
            } else {
                this.disablePanelMode();
            }
        },
    },

    methods: {
        async resolveDiagramData() {
            if (hasLivingPersonAccess(this.authToken)) {
                return fetchBloodRelativesStats({ includeGroups: true });
            }

            try {
                return await fetchBloodRelativesStaticFile();
            } catch (staticError) {
                console.warn('Static blood-relatives data unavailable; falling back to API.', staticError);
                return fetchBloodRelativesStats({ includeGroups: true });
            }
        },

        async loadData() {
            debugLog('Loading data...');
            this.loading = true;
            this.error = null;
            try {
                // Profile photo and graph payload are independent, so load in parallel.
                const [diagramData, selfPhoto] = await Promise.all([
                    this.resolveDiagramData(),
                    fetchPersonDefaultPhoto('I43').catch(() => null),
                ]);

                debugLog('Diagram data loaded:', diagramData);
                debugLog('Self photo:', selfPhoto);

                this.selfPhotoUrl = selfPhoto?.path ? this.toPhotoUrl(selfPhoto.path) : null;

                const flattened = this.flattenNodes(diagramData?.nodes || []);
                debugLog('Flattened nodes count:', flattened.length);

                this.treeTotal = Number(diagramData?.total || flattened.reduce((sum, node) => sum + node.count, 0));
                this.nodes = this.decorateNodes(flattened);
                debugLog('Decorated nodes count:', this.nodes.length);

                this.groupsByKey = this.buildGroupsIndex(diagramData?.groups || []);
                debugLog('groupsByKey initialized with keys:', Object.keys(this.groupsByKey).slice(0, 10));
                debugLog('Total groups:', Object.keys(this.groupsByKey).length);
                this.applyCanvasBounds();
                this.nodesByPath = this.nodes.reduce((acc, node) => {
                    acc[node.path] = node;
                    return acc;
                }, {});
                this.nodesByKey = this.nodes.reduce((acc, node) => {
                    acc[node.key] = node;
                    return acc;
                }, {});
                const baseConnectors = this.nodes
                    .map((node) => this.createConnector(node))
                    .filter(Boolean);
                this.connectors = [
                    ...baseConnectors,
                    ...this.createHalfConnectors(),
                ];

                this.initialView();
                debugLog('Data loading complete - initialView called');
            } catch (err) {
                console.error('loadData error:', err);
                this.error = this.$t('bloodRelativesDiagram.errors.loadFailed');
            } finally {
                debugLog('loadData finally block - loading set to false');
                this.loading = false;
            }
        },

        buildGroupsIndex(groups) {
            debugLog('buildGroupsIndex called with groups count:', Array.isArray(groups) ? groups.length : 'not-array');
            const index = {};
            if (!Array.isArray(groups)) {
                debugLog('buildGroupsIndex: groups is not an array, returning empty index');
                return index;
            }

            groups.forEach((group, idx) => {
                const path = this.normalizePath(group.path || '');
                if (!path) {
                    debugLog(`buildGroupsIndex: group ${idx} has invalid path`);
                    return;
                }
                const half = Boolean(group.half);
                const key = `${path}|${half ? '1' : '0'}`;
                const individuals = Array.isArray(group.individuals) ? group.individuals : [];
                debugLog(`buildGroupsIndex: group ${idx} path=${path} half=${half} individuals=${individuals.length}`);

                index[key] = individuals.map((person) => {
                    if (isLivingPlaceholder(person)) return person;
                    return {
                        personID: person.personID,
                        name: person.name || [person.firstname, person.lastname].filter(Boolean).join(' ').trim() || person.personID,
                        sex: person.sex || null,
                        living: Boolean(person.living),
                        birthdatetr: person.birthdatetr || null,
                        altbirthdatetr: person.altbirthdatetr || null,
                        deathdatetr: person.deathdatetr || null,
                        burialdatetr: person.burialdatetr || null,
                        birthYear: person.birthYear ?? null,
                        deathYear: person.deathYear ?? null,
                        photoPath: person.photoPath || person.path || null,
                    };
                });
            });

            return index;
        },

        flattenNodes(items, target = []) {
            // Backend data is hierarchical; flatten once so rendering and indexing stay simple.
            if (!Array.isArray(items)) return target;
            for (const item of items) {
                const path = this.normalizePath(item.path || '');
                if (!path) continue;
                target.push({
                    path,
                    count: Number(item.count || 0),
                    label: item.label || null,
                    half: Boolean(item.half),
                });
                if (Array.isArray(item.children) && item.children.length) {
                    this.flattenNodes(item.children, target);
                }
            }
            return target;
        },

        normalizePath(path) {
            return String(path || '').replace(/^path_/, '').replace(/[^aBA]/g, '');
        },

        getAncestorDepth(path) {
            return (String(path).match(/^a+/) || [''])[0].length;
        },

        getDescendantSteps(path) {
            const p = String(path || '');
            const aPrefix = this.getAncestorDepth(p);
            const suffix = p.slice(aPrefix);
            const hasSibling = suffix.includes('B') ? 1 : 0;
            const descentDepth = (suffix.match(/A/g) || []).length;
            return hasSibling + descentDepth;
        },

        pathToGrid(path, isHalf) {
            const row = this.getAncestorDepth(path);
            const descCount = this.getDescendantSteps(path);
            return {
                row,
                col: isHalf ? (-descCount - 1) : descCount,
            };
        },

        pathToPosition(path, isHalf) {
            // Path syntax encodes ancestry/sibling/descendant transitions and maps directly
            // to diagram coordinates.
            const selfX = this.selfPosition?.x ?? SELF_X;
            const p = String(path || '');
            const ancestorDepth = (p.match(/^a*/) || [''])[0].length;
            const suffix = p.slice(ancestorDepth);
            const hasSibling = suffix.includes('B') ? 1 : 0;
            const descentDepth = (suffix.match(/A/g) || []).length;

            const colX = ancestorDepth + hasSibling;
            const y = SELF_Y - ((ancestorDepth - descentDepth) * STEP_Y);

            if (isHalf) {
                return {
                    x: selfX - (colX * STEP_X),
                    y,
                    ancestorDepth,
                    hasSibling,
                    descentDepth,
                    descendantSteps: hasSibling + descentDepth,
                    totalCol: -colX,
                };
            }

            return {
                x: selfX + (colX * STEP_X),
                y,
                ancestorDepth,
                hasSibling,
                descentDepth,
                descendantSteps: hasSibling + descentDepth,
                totalCol: colX,
            };
        },

        decorateNodes(flatNodes) {
            // Attach render metadata so template loops do not recalculate geometry.
            return flatNodes.map((node) => {
                const pos = this.pathToPosition(node.path, node.half);
                return {
                    ...node,
                    ancestorDepth: pos.ancestorDepth,
                    row: pos.ancestorDepth,
                    col: pos.totalCol,
                    descSteps: pos.descendantSteps,
                    key: `${node.path}-${node.half ? 'half' : 'full'}`,
                    label: this.pathToLabel(node.path, node.half),
                    x: pos.x,
                    y: pos.y,
                };
            });
        },

        applyCanvasBounds() {
            const maxColX = Math.max(0, ...this.nodes.map(n => Math.abs(n.totalCol || 0)));
            const maxBelowU = Math.max(0, ...this.nodes.map(n => (
                Math.max(0, Math.round((n.y - SELF_Y) / STEP_Y))
            )));

            this.canvas.width = (maxColX * STEP_X * 2) + NODE_W + 400;
            this.canvas.height = SELF_Y + (maxBelowU * STEP_Y) + 300;

            const centeredSelfX = Math.round(this.canvas.width / 2);
            this.selfPosition = {
                x: centeredSelfX,
                y: SELF_Y,
            };

            this.nodes = this.nodes.map((node) => {
                const pos = this.pathToPosition(node.path, node.half);
                return {
                    ...node,
                    ancestorDepth: pos.ancestorDepth,
                    x: pos.x,
                    y: pos.y,
                    col: pos.totalCol,
                    row: pos.ancestorDepth,
                };
            });
        },

        pathToLabel(path, half) {
            return this.getRelationshipLabel(path, half);
        },

        getRelationshipLabel(path, isHalf) {
            const p = String(path || '');
            const ancestorDepth = (p.match(/^a*/) || [''])[0].length;
            const suffix = p.slice(ancestorDepth);
            const hasSibling = suffix.includes('B') ? 1 : 0;
            const descentDepth = (suffix.match(/A/g) || []).length;
            const dSteps = hasSibling + descentDepth;

            if (dSteps === 0) {
                return this.applyHalfPrefix(
                    this.getAncestorLabelByDepth(ancestorDepth),
                    isHalf,
                );
            }

            if (ancestorDepth === 0 && hasSibling === 1) {
                if (descentDepth === 0) {
                    return this.applyHalfPrefix(this.$t('bloodRelativesDiagram.relationships.siblings'), isHalf);
                }
                if (descentDepth === 1) {
                    return this.applyHalfPrefix(this.$t('bloodRelativesDiagram.relationships.children'), isHalf);
                }
                if (descentDepth === 2) {
                    return this.applyHalfPrefix(this.$t('bloodRelativesDiagram.relationships.grandchildren'), isHalf);
                }
                return this.applyHalfPrefix(
                    this.$t('bloodRelativesDiagram.relationships.descendants', { n: descentDepth }),
                    isHalf,
                );
            }

            if (hasSibling === 1 && descentDepth === 0) {
                return this.getSiblingOfAncestorLabel(ancestorDepth, isHalf);
            }

            const userGens = ancestorDepth + 1;
            const relativeGens = dSteps;
            const cousinDegree = Math.min(userGens, relativeGens) - 1;
            const removal = relativeGens - userGens;

            if (cousinDegree < 1) {
                return isHalf
                    ? this.$t('bloodRelativesDiagram.relationships.halfRelatives')
                    : this.$t('bloodRelativesDiagram.relationships.relatives');
            }

            const baseCousin = this.getCousinLabel(cousinDegree, isHalf);

            if (removal === 0) {
                return this.ensureLabelCapitalized(baseCousin);
            }

            if (removal < 0) {
                const context = this.getAncestorLabelByDepth(Math.abs(removal)).toLowerCase();
                return this.ensureLabelCapitalized(
                    this.$t('bloodRelativesDiagram.relationships.cousinOf', { cousin: baseCousin, ancestor: context })
                );
            }

            const lowerCousin = baseCousin.charAt(0).toLowerCase() + baseCousin.slice(1);
            if (removal === 1) {
                return this.ensureLabelCapitalized(
                    this.$t('bloodRelativesDiagram.relationships.childrenOf', { cousin: lowerCousin })
                );
            }
            if (removal === 2) {
                return this.ensureLabelCapitalized(
                    this.$t('bloodRelativesDiagram.relationships.grandchildrenOf', { cousin: lowerCousin })
                );
            }
            return this.ensureLabelCapitalized(
                this.$t('bloodRelativesDiagram.relationships.descendantsOf', { n: removal, cousin: lowerCousin })
            );
        },

        getSiblingOfAncestorLabel(ancestorDepth, isHalf) {
            if (ancestorDepth === 1) {
                return this.applyHalfPrefix(this.$t('bloodRelativesDiagram.relationships.unclesAunts'), isHalf);
            }
            if (ancestorDepth === 2) {
                return this.applyHalfPrefix(this.$t('bloodRelativesDiagram.relationships.greatUnclesAunts'), isHalf);
            }
            const ancestorLabel = this.getAncestorLabelByDepth(ancestorDepth).toLowerCase();
            return this.applyHalfPrefix(
                this.$t('bloodRelativesDiagram.relationships.siblingsOfAncestor', { ancestor: ancestorLabel }),
                isHalf,
            );
        },

        getCousinLabel(degree, isHalf) {
            if (degree === 1) {
                return isHalf
                    ? this.$t('bloodRelativesDiagram.relationships.halfCousins')
                    : this.$t('bloodRelativesDiagram.relationships.cousins');
            }
            if (degree === 2) {
                return isHalf
                    ? this.$t('bloodRelativesDiagram.relationships.halfSecondCousins')
                    : this.$t('bloodRelativesDiagram.relationships.secondCousins');
            }
            const degWord = this.degreeWord(degree);
            return isHalf
                ? this.$t('bloodRelativesDiagram.relationships.halfNthCousins', { degree: degWord })
                : this.$t('bloodRelativesDiagram.relationships.nthCousins', { degree: degWord });
        },

        ancestorContextFromRemoval(removal) {
            if (removal <= 1) return 'ouders';
            if (removal === 2) return 'grootouders';
            if (removal === 3) return 'overgrootouders';
            return `${removal - 2}e overgrootouders`;
        },

        degreeWord(n) {
            if (n >= 2 && n <= 10) {
                return this.$t(`bloodRelativesDiagram.relationships.degreeWords.${n}`);
            }
            return this.$t('bloodRelativesDiagram.relationships.degreeWords.fallback', { n });
        },

        getAncestorLabelByDepth(aDepth) {
            if (aDepth >= 1 && aDepth <= 16) {
                return this.$t(`bloodRelativesDiagram.relationships.ancestorDepth.${aDepth}`);
            }
            return this.$t('bloodRelativesDiagram.relationships.ancestorDepth.fallback', { n: aDepth });
        },

        applyHalfPrefix(label, isHalf) {
            const normalized = this.ensureLabelCapitalized(String(label || '').trim());
            if (!isHalf) return normalized;
            if (!normalized) return this.$t('bloodRelativesDiagram.relationships.halfRelatives');
            const locale = this.$i18n.locale;
            if (locale === 'id') {
                return `${normalized} tiri`;
            }
            if (locale === 'en') {
                const lower = normalized.charAt(0).toLowerCase() + normalized.slice(1);
                return this.ensureLabelCapitalized(`Half-${lower}`);
            }
            // nl: fuse 'Half' prefix (Dutch morphology)
            if (/^half/i.test(normalized)) return this.ensureLabelCapitalized(normalized);
            const withHalf = `Half${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`;
            return this.ensureLabelCapitalized(withHalf);
        },

        ensureLabelCapitalized(label) {
            const text = String(label || '').trim();
            if (!text) return text;
            return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
        },


        ordinalDutch(n) {
            const ordinals = {
                1: '1e',
                2: '2e',
                3: '3e',
                4: '4e',
                5: '5e',
                6: '6e',
                7: '7de',
                8: '8e',
                9: '9e',
                10: '10e',
            };
            return ordinals[n] || `${n}e`;
        },

        labelFontStyle(label) {
            const length = String(label || '').length;
            if (length > 35) return { fontSize: '0.65rem' };
            return {};
        },

        isSpineNode(node) {
            return /^a+$/.test(String(node?.path || ''));
        },

        parentPath(path) {
            if (!path) return null;
            if (/^a+$/.test(path)) {
                return path.length === 1 ? 'self' : path.slice(0, -1);
            }
            if (path.endsWith('A')) {
                return path.slice(0, -1);
            }
            if (path.endsWith('B')) {
                const parent = path.slice(0, -1);
                return parent || 'self';
            }
            return null;
        },

        createConnector(node) {
            if (node.half) return null;
            const p = String(node.path || '');
            const ancestorDepth = (p.match(/^a*/) || [''])[0].length;
            const suffix = p.slice(ancestorDepth);
            const findNode = (path) => this.nodesByKey[`${path}-full`] || null;

            // Build connector paths from compact path semantics to deterministic SVG lines.
            // U -> Ouders: elbow (up then right to diagonal position)
            if (p === 'a') {
                return {
                    key: `self-${node.key}`,
                    half: false,
                    parentPath: 'self',
                    childPath: p,
                    parentKey: 'self',
                    childKey: node.key,
                    d: `M ${this.selfNode.x + (NODE_W / 2)} ${this.selfNode.y} V ${node.y + (NODE_H / 2)} H ${node.x + (NODE_W / 2)}`,
                };
            }

            // U -> Broers/zussen: arch up then right then down
            if (p === 'B') {
                return {
                    key: `self-${node.key}`,
                    half: false,
                    parentPath: 'self',
                    childPath: p,
                    parentKey: 'self',
                    childKey: node.key,
                    d: `M ${node.x + NODE_W / 2} ${this.selfNode.y + NODE_H - 140} V ${node.y + NODE_H / 2}`,
                };
            }

            // Ancestor spine (aa, aaa...): diagonal elbow (up then right)
            if (/^a+$/.test(p) && ancestorDepth > 1) {
                const parentPath = 'a'.repeat(ancestorDepth - 1);
                const parentNode = findNode(parentPath);
                if (!parentNode) return null;
                return {
                    key: `${parentNode.key}-${node.key}`,
                    half: false,
                    parentPath,
                    childPath: p,
                    parentKey: parentNode.key,
                    childKey: node.key,
                    d: `M ${parentNode.x + (NODE_W / 2)} ${parentNode.y} V ${node.y + (NODE_H / 2)} H ${node.x + (NODE_W / 2)}`,
                };
            }

            // "xB" nodes: arch up then right then down
            if (suffix === 'B') {
                const parentPath = ancestorDepth > 0 ? 'a'.repeat(ancestorDepth) : 'self';
                const parentNode = parentPath === 'self' ? this.selfNode : findNode(parentPath);
                if (!parentNode) return null;
                return {
                    key: `${parentPath}-${node.key}`,
                    half: false,
                    parentPath,
                    childPath: p,
                    parentKey: parentPath === 'self' ? 'self' : `${parentPath}-full`,
                    childKey: node.key,
                    d: `M ${node.x + NODE_W / 2} ${parentNode.y + NODE_H - 140} V ${node.y + NODE_H / 2}`,
                };
            }

            // "xBA..." nodes ending in A: vertical from parent's bottom
            if (suffix.endsWith('A')) {
                const parentPath = p.slice(0, -1);
                const parentNode = parentPath ? findNode(parentPath) : null;
                if (!parentNode) return null;
                return {
                    key: `${parentPath}-${node.key}`,
                    half: false,
                    parentPath,
                    childPath: p,
                    parentKey: `${parentPath}-full`,
                    childKey: node.key,
                    d: `M ${parentNode.x + (NODE_W / 2)} ${parentNode.y + NODE_H} V ${node.y + (NODE_H / 2)}`,
                };
            }

            return null;
        },

        createHalfConnectors() {
            // Half-relative branches are chained separately so they can be styled and
            // toggled independently from full-blood branches.
            const halfNodes = this.nodes.filter(n => n.half);
            const halfGroups = {};
            halfNodes.forEach((node) => {
                const k = String(node.ancestorDepth);
                if (!halfGroups[k]) halfGroups[k] = [];
                halfGroups[k].push(node);
            });

            const connectors = [];

            Object.entries(halfGroups).forEach(([depth, nodes]) => {
                const aDepth = Number(depth);

                const spineAncestor = this.nodes.find(n => (
                    !n.half && /^a+$/.test(n.path) && n.ancestorDepth === aDepth
                ));

                const sorted = [...nodes].sort((a, b) => a.y - b.y || a.path.localeCompare(b.path));

                sorted.forEach((node, i) => {
                    if (i === 0) {
                        if (spineAncestor) {
                            connectors.push({
                                key: `half-anchor-${depth}-${node.key}`,
                                d: `M ${spineAncestor.x} ${spineAncestor.y + (NODE_H / 2)} H ${node.x + NODE_W}`,
                                half: true,
                                parentPath: spineAncestor.path,
                                childPath: node.path,
                                parentKey: spineAncestor.key,
                                childKey: node.key,
                            });
                        } else {
                            connectors.push({
                                key: `half-anchor-${depth}-${node.key}`,
                                d: `M ${this.selfNode.x} ${this.selfNode.y + (NODE_H / 2)} H ${node.x + NODE_W}`,
                                half: true,
                                parentPath: 'self',
                                childPath: node.path,
                                parentKey: 'self',
                                childKey: node.key,
                            });
                        }
                    } else {
                        const prev = sorted[i - 1];
                        connectors.push({
                            key: `half-chain-${prev.key}-${node.key}`,
                            d: `M ${prev.x + (NODE_W / 2)} ${prev.y + NODE_H} V ${node.y + (NODE_H / 2)}`,
                            half: true,
                            parentPath: prev.path,
                            childPath: node.path,
                            parentKey: prev.key,
                            childKey: node.key,
                        });
                    }
                });
            });

            return connectors;
        },

        nodePositionStyle(node) {
            return {
                left: `${node.x}px`,
                top: `${node.y}px`,
            };
        },

        showTooltip(event, node) {
            this.tooltip.visible = true;
            this.tooltip.text = this.tooltipText(node);
            this.moveTooltip(event);
        },

        moveTooltip(event) {
            this.tooltip.x = event.clientX + 12;
            this.tooltip.y = event.clientY + 12;
        },

        hideTooltip() {
            this.tooltip.visible = false;
        },

        tooltipText(node) {
            const percentage = this.treeTotal > 0
                ? Math.round((node.count / this.treeTotal) * 100)
                : 0;
            if (node.count === 1) {
                return this.$t('bloodRelativesDiagram.tooltip.viewOne', { pct: percentage });
            }
            return this.$t('bloodRelativesDiagram.tooltip.viewMultiple', { count: node.count, pct: percentage });
        },

        async listIndividuals(path, label, half = null) {
            debugLog('listIndividuals called:', { path, label, half });
            debugLog('groupsByKey:', this.groupsByKey);

            this.panel.visible = true;
            this.panel.loading = false;
            this.panel.error = null;
            this.panel.individuals = [];
            this.panel.title = label;

            try {
                const key = `${this.normalizePath(path)}|${half ? '1' : '0'}`;
                debugLog('Looking up key:', key);
                const rawIndividuals = this.groupsByKey[key] || [];
                debugLog('Found individuals:', rawIndividuals);

                this.panel.individuals = [...rawIndividuals].sort((a, b) => {
                    return String(a.name).localeCompare(String(b.name));
                });
                if (!this.panel.individuals.length) {
                    this.panel.error = this.$t('bloodRelativesDiagram.errors.noPeopleInGroup');
                }
            } catch (err) {
                console.error('Error in listIndividuals:', err);
                this.panel.error = this.$t('bloodRelativesDiagram.errors.couldNotLoad');
            }
        },

        closePanel() {
            this.panel.visible = false;
        },

        enablePanelMode() {
            this.pointer.dragging = false;
            this.pointer.pointerId = null;

            document.addEventListener('keydown', this.onPanelEscKey);
            this.lockPageScroll();
        },

        disablePanelMode() {
            document.removeEventListener('keydown', this.onPanelEscKey);
            this.unlockPageScroll();
        },

        lockPageScroll() {
            if (this.pageScrollLock.active) return;
            this.pageScrollLock.previousOverflow = document.body.style.overflow || '';
            this.pageScrollLock.previousTouchAction = document.body.style.touchAction || '';
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
            this.pageScrollLock.active = true;
        },

        unlockPageScroll() {
            if (!this.pageScrollLock.active) return;
            document.body.style.overflow = this.pageScrollLock.previousOverflow;
            document.body.style.touchAction = this.pageScrollLock.previousTouchAction;
            this.pageScrollLock.previousOverflow = '';
            this.pageScrollLock.previousTouchAction = '';
            this.pageScrollLock.active = false;
        },

        onPanelEscKey(event) {
            if (event.key === 'Escape') this.closePanel();
        },

        formatPersonYears(person) {
            if (isLivingPlaceholder(person)) return '';
            const birth = person.birthdatetr || (person.birthYear ? `${person.birthYear}-00-00` : null);
            const death = person.deathdatetr || (person.deathYear ? `${person.deathYear}-00-00` : null);
            return formatYears(
                birth,
                person.altbirthdatetr || null,
                death,
                person.burialdatetr || null,
                person.living ? 1 : 0
            );
        },

        getPersonNodeKey(person) {
            return getPersonNodeKey(person);
        },

        toPhotoUrl(photoPath) {
            if (!photoPath) return null;
            if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) return photoPath;
            const base = process.env.VUE_APP_PHOTO_API_URL || 'http://localhost:8082/photos/';
            return `${base}${encodeURIComponent(photoPath)}`;
        },

        scheduleTransform(nextScale, nextPanX, nextPanY) {
            this.rafState.nextScale = nextScale;
            this.rafState.nextPanX = nextPanX;
            this.rafState.nextPanY = nextPanY;
            if (this.rafState.transformRaf) return;
            this.rafState.transformRaf = requestAnimationFrame(() => {
                this.scale = this.rafState.nextScale;
                this.panX = this.rafState.nextPanX;
                this.panY = this.rafState.nextPanY;
                this.rafState.transformRaf = 0;
            });
        },

        zoomBy(delta, clientX = null, clientY = null) {
            const viewportRect = this.$refs.viewport?.getBoundingClientRect();
            if (!viewportRect) return;

            const targetScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));
            if (targetScale === this.scale) return;

            const pointerX = clientX !== null ? clientX - viewportRect.left : viewportRect.width / 2;
            const pointerY = clientY !== null ? clientY - viewportRect.top : viewportRect.height / 2;

            // Zoom around the cursor (or viewport center) by preserving the world point
            // beneath the pointer while changing scale.
            const worldX = (pointerX - this.panX) / this.scale;
            const worldY = (pointerY - this.panY) / this.scale;

            const nextPanX = pointerX - (worldX * targetScale);
            const nextPanY = pointerY - (worldY * targetScale);
            this.scheduleTransform(targetScale, nextPanX, nextPanY);
        },

        zoomIn() {
            this.zoomBy(0.1);
        },

        zoomOut() {
            this.zoomBy(-0.1);
        },

        onWheel(event) {
            const target = event.target instanceof Element ? event.target : null;

            if (this.panel.visible) {
                if (!target?.closest('.relatives-panel__body')) {
                    event.preventDefault();
                }
                return;
            }

            if (target?.closest('.relatives-panel')) {
                return;
            }

            if (this.cooperativeGestures && !event.ctrlKey && !event.metaKey) {
                return;
            }

            event.preventDefault();
            const delta = event.deltaY < 0 ? 0.07 : -0.07;
            this.zoomBy(delta, event.clientX, event.clientY);
        },

        onPointerDown(event) {
            if (this.panel.visible) {
                return;
            }

            // Ignore native interactive elements so click handlers keep expected behavior.
            const interactiveTarget = event.target?.closest('button, a, input, textarea, select, [data-no-pan]');
            if (interactiveTarget) return;

            this.pointer.dragging = true;
            this.pointer.pointerId = event.pointerId;
            this.pointer.startX = event.clientX;
            this.pointer.startY = event.clientY;
            this.pointer.startPanX = this.panX;
            this.pointer.startPanY = this.panY;
            event.currentTarget.setPointerCapture(event.pointerId);
        },

        onPointerMove(event) {
            if (!this.pointer.dragging || event.pointerId !== this.pointer.pointerId) return;
            const dx = event.clientX - this.pointer.startX;
            const dy = event.clientY - this.pointer.startY;
            this.scheduleTransform(this.scale, this.pointer.startPanX + dx, this.pointer.startPanY + dy);
        },

        onPointerUp(event) {
            if (event.pointerId !== this.pointer.pointerId) return;
            this.pointer.dragging = false;
            this.pointer.pointerId = null;
        },

        initialView() {
            // Open at 100% zoom centred on Jeffrey (the self node).
            const viewportRect = this.$refs.viewport?.getBoundingClientRect();
            const viewportWidth = viewportRect?.width || this.viewport.width;
            const viewportHeight = viewportRect?.height || this.viewport.height;
            if (!viewportWidth || !viewportHeight) return;

            const targetScale = 1;
            const selfX = this.selfPosition.x;
            const selfY = this.selfPosition.y;
            const nextPanX = (viewportWidth / 2) - selfX * targetScale;
            const nextPanY = (viewportHeight / 2) - selfY * targetScale;

            this.scheduleTransform(targetScale, nextPanX, nextPanY);
        },

        enterPseudoFullscreen() {
            this.savedScrollY = window.scrollY || window.pageYOffset || 0;
            document.body.classList.add('BloodRelatives_body_pseudo_fullscreen');
            document.body.style.top = `-${this.savedScrollY}px`;
            this.isPseudoFullscreen = true;
        },
        disablePseudoFullscreen() {
            if (!this.isPseudoFullscreen) return;
            document.body.classList.remove('BloodRelatives_body_pseudo_fullscreen');
            document.body.style.top = '';
            window.scrollTo(0, this.savedScrollY || 0);
            this.savedScrollY = 0;
            this.isPseudoFullscreen = false;
        },
        toggleFullscreen() {
            const container = this.$refs.diagramContainer;
            const fullscreenElement =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;

            if (this.isPseudoFullscreen) {
                this.disablePseudoFullscreen();
                return;
            }

            if (!fullscreenElement) {
                if (!container) return;
                const requestFullscreen =
                    container.requestFullscreen ||
                    container.webkitRequestFullscreen ||
                    container.msRequestFullscreen;
                if (requestFullscreen) {
                    const result = requestFullscreen.call(container);
                    if (result && typeof result.catch === 'function') {
                        result.catch(() => this.enterPseudoFullscreen());
                    }
                    return;
                }
                this.enterPseudoFullscreen();
                return;
            }

            const exitFullscreen =
                document.exitFullscreen ||
                document.webkitExitFullscreen ||
                document.msExitFullscreen;
            if (exitFullscreen) exitFullscreen.call(document);
        },
        onFullscreenChange() {
            this.isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            if (this.isFullscreen) this.disablePseudoFullscreen();
        },

        updateViewportSize() {
            const rect = this.$refs.viewport?.getBoundingClientRect();
            if (!rect) return;
            this.viewport.width = Math.round(rect.width);
            this.viewport.height = Math.round(rect.height);
        },

    },

    async mounted() {
        debugLog('BloodRelativesDiagram mounted');
        await this.loadData();
        this.$nextTick(() => {
            // Observe viewport size changes so virtualization and centering remain accurate.
            this.updateViewportSize();
            this.initialView();
            if (this.$refs.viewport) {
                this.resizeObserver = new ResizeObserver(() => this.updateViewportSize());
                this.resizeObserver.observe(this.$refs.viewport);
            }
        });
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
        document.addEventListener('msfullscreenchange', this.onFullscreenChange);
    },

    beforeUnmount() {
        this.disablePseudoFullscreen();
        this.disablePanelMode();
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
        document.removeEventListener('msfullscreenchange', this.onFullscreenChange);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.rafState.transformRaf) {
            cancelAnimationFrame(this.rafState.transformRaf);
        }
    },
};
</script>

<style scoped>
:global(:root) {
    --node-bg: #ffffff;
    --node-border: #f5a0a3;
    --node-count-color: #ae1e25;
    --node-label-color: #6b7280;
    --node-half-border: #d94f55;
    --node-spine-bg: #fff5f5;
    --connector-color: #f5a0a3;
    --connector-half-color: #d94f55;
    --self-ring: #ae1e25;
    --self-glow: #fde8e9;
}

.blood-relatives-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    overflow: hidden;
}

.blood-relatives-wrap--fullscreen,
.blood-relatives-wrap:fullscreen {
    border-radius: 0;
}

.blood-relatives-wrap--fullscreen {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    z-index: 9999;
    background: transparent;
}

.blood-relatives-wrap--fullscreen .diagram-viewport,
.blood-relatives-wrap:fullscreen .diagram-viewport {
    flex: 1;
    height: 0;
    border-radius: 0;
}

:global(body.BloodRelatives_body_pseudo_fullscreen) {
    position: fixed;
    overflow: hidden;
    inset: 0;
    width: 100%;
}

#zoom-buttons {
    position: absolute;
    bottom: 24px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 100;
}

#zoom-buttons button {
    padding: 0;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0);
    border: none;
    border-radius: var(--border-radius-pill);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
}

#zoom-buttons button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button {
    position: absolute;
    top: 24px;
    right: 24px;
    height: 28px;
    width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: var(--border-radius-pill);
    box-shadow: 0 0 0 0 var(--brand-color-1);
    z-index: 100;
}

#fullscreen-button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button img {
    width: 16px;
    height: 16px;
}

.status {
    background: #fff;
    border-radius: var(--border-radius-lg);
    padding: 14px;
    color: #555;
}

.status--error {
    color: #b84040;
}

.diagram-viewport {
    position: relative;
    height: min(74vh, 860px);
    overflow: hidden;
    /* border-radius: 16px; */
    border: 1px solid #e0e0e0;
    background: transparent;
    touch-action: none;
    cursor: grab;
}

.diagram-zoom-hint {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 10;
    max-width: min(420px, calc(100% - 112px));
    padding: 8px 12px;
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.92);
    color: var(--brand-text-dark);
    font-size: 0.78rem;
    line-height: 1.35;
    pointer-events: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.diagram-viewport--panel-open {
    overflow: hidden;
    cursor: default;
}

.diagram-viewport:active {
    cursor: grabbing;
}

.diagram-canvas-shell {
    position: relative;
    width: 100%;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
}

.diagram-world {
    position: absolute;
    left: 0;
    top: 0;
    transform-origin: 0 0;
    will-change: transform;
}

.diagram-connectors {
    position: absolute;
    left: 0;
    top: 0;
    overflow: visible;
}

.connector {
    fill: none;
    stroke: var(--connector-color);
    stroke-width: 1.5;
}

.connector.half {
    fill: none;
    stroke: var(--connector-half-color);
    stroke-width: 1.5;
    stroke-dasharray: 5 4;
}

.self-node {
    position: absolute;
    width: 110px;
    min-height: 90px;
    text-align: center;
    z-index: 3;
}

.self-avatar {
    width: 86px;
    height: 86px;
    border-radius: 50%;
    object-fit: cover;
    padding: 3px;
    border: 3px solid var(--self-ring);
    box-shadow: 0 0 0 4px var(--self-glow);
    background: #fff;
    margin: auto;
}

.self-avatar--fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;
    color: #2b6a6a;
}

.self-label {
    margin-top: 8px;
    color: var(--self-ring);
    font-size: 0.85rem;
    font-weight: 700;
}

.relative-node {
    position: absolute;
    transform: scale(1);
    transform-origin: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 110px;
    min-height: 90px;
    border-radius: 16px;
    border: 2px solid var(--node-border);
    background: var(--node-bg);
    box-shadow: 0 2px 8px rgba(174, 30, 37, 0.1);
    padding: 10px 12px;
    text-align: center;
    cursor: pointer;
    z-index: 2;
    transition: all 150ms ease;
}

.relative-node:hover {
    background: #fff0f0;
    border-color: var(--node-count-color);
    transform: scale(1.04);
}

.relative-node:focus-visible {
    outline: 2px solid var(--node-count-color);
    outline-offset: 2px;
}

.relative-node--spine {
    background: var(--node-spine-bg);
    border-color: var(--node-half-border);
}

.relative-node--half {
    position: absolute;
    border-style: dashed;
    border-color: var(--node-half-border);
    background: #fffafa;
}

.relative-node--half::after {
    content: '½';
    position: absolute;
    top: -6px;
    right: -6px;
    border-radius: 999px;
    padding: 1px 5px;
    background: var(--brand-color-3);
    color: var(--brand-text-dark);
    font-size: 0.6rem;
    line-height: 1.2;
    font-weight: 700;
}

.relative-count {
    color: var(--node-count-color);
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1;
}

.relative-node--half .relative-count {
    color: #c0363d;
}

.relative-label {
    color: var(--node-label-color);
    font-size: 0.72rem;
    font-weight: 400;
    line-height: 1.2;
    text-align: center;
    overflow-wrap: anywhere;
}

.relative-node--half .relative-label {
    line-height: 1.3;
}

.node-tooltip {
    position: fixed;
    z-index: 20;
    pointer-events: none;
    background: #1f1f1f;
    color: #fff;
    border-radius: var(--border-radius-md);
    font-size: 0.78rem;
    padding: 8px 10px;
    max-width: 260px;
}

.relatives-panel {
    position: absolute;
    inset: 0;
    z-index: 30;
    pointer-events: all;
    --relatives-panel-width: min(420px, 100vw);
}

.relatives-panel__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.48);
}

.relatives-panel__close-ext {
    position: absolute;
    top: 32px;
    right: calc(var(--relatives-panel-width) + 12px);
    width: 40px;
    height: 40px;
    padding: 10px;
    border-radius: var(--border-radius-md);
    border: none;
    background: #fff;
    color: var(--brand-text-dark);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1;
    transition: var(--transition-ease);
}

.relatives-panel__close-ext:hover {
    background: #f0f0f0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.relatives-panel__sheet {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: var(--relatives-panel-width);
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
}

.relatives-panel-enter-active,
.relatives-panel-leave-active {
    transition: opacity 0.25s ease;
}

.relatives-panel-enter-from,
.relatives-panel-leave-to {
    opacity: 0;
}

.relatives-panel-enter-active .relatives-panel__overlay,
.relatives-panel-leave-active .relatives-panel__overlay {
    transition: opacity 0.25s ease;
}

.relatives-panel-enter-from .relatives-panel__overlay,
.relatives-panel-leave-to .relatives-panel__overlay {
    opacity: 0;
}

.relatives-panel-enter-active .relatives-panel__sheet,
.relatives-panel-leave-active .relatives-panel__sheet {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.relatives-panel-enter-from .relatives-panel__sheet,
.relatives-panel-leave-to .relatives-panel__sheet {
    transform: translateX(100%);
}

.relatives-panel__header {
    padding: 24px 20px 16px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
}

.relatives-panel__title {
    margin: 0 0 4px;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1a1a1a;
}

.relatives-panel__subtitle {
    margin: 0;
    font-size: 0.85rem;
    color: #888;
}

.relatives-panel__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
}

.relatives-panel__body--status {
    display: flex;
    align-items: center;
    justify-content: center;
}

.relatives-panel__error {
    color: #b84040;
    font-size: 0.9rem;
}

.relatives-panel__cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

@media (max-width: 520px) {
    .relatives-panel__close-ext {
        right: 12px;
    }

    .relatives-panel__sheet {
        width: 100vw;
    }
}

@media (max-width: 900px) {
    .relative-node {
        width: 108px;
    }

    .diagram-viewport {
        height: 68vh;
    }
}

@media (max-width: 860px) {
    #fullscreen-button {
        top: 16px;
        right: 16px;
    }

    #zoom-buttons {
        bottom: 16px;
        right: 16px;
    }
}
</style>
